import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import withEventTrackingLogEvent from 'EventTracking/withEventTrackingLogEvent';
import { uploadFileRaw } from 'Helpers/File';
import { noop } from 'Helpers/PropTypes';
import { FILE_UPLOAD_TYPES } from 'Consts/Queries';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useUserContext from 'Context/UserContext';
import fr from 'facet-rest';

// This is a really quick and dirty port of the Uploader component to move it over to a function component done during the
// PX release so that I can use hooks. Much clean up still to be done here, but the intention is to eventually deprecate
// the old Uploader and replace it with this one after the initial launch.
// Beware, this component replaces the onClick element of its immediate child component with handleSubmit, so it is really
// only intended to be used with a single child component rather than arbitrary children.
const Uploader2 = (props) => {
  const {
    additionalFormData,
    params,
    setUploadProgress,
    onCatch,
    onFinally,
    onStart,
    onSuccess,
    successToastMessage,
    displaySuccessToastMessage,
    method,
    EventTracking,
    children,
    disableClick,
    onClick,
    accept,
    url: propsUrl,
  } = props;
  const [url, setUrl] = useState(propsUrl);
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const fileInputRef = useRef();
  const { userResource } = useUserContext();

  useEffect(() => {
    if (!url && userResource) {
      setUrl(userResource?.relationship(fr.relationships.uploadFile.name).link('related').url());
    }
  }, [userResource]);

  if (!url) {
    return null;
  }

  function handleSubmit(e) {
    const {
      logEvent,
      logEventSuccess,
      logEventFailureWithError,
    } = EventTracking;

    const { files } = e.target;

    if (!files.length) {
      return Promise.resolve();
    }

    const fileArray = [];

    for (let i = 0; i < files.length; i++) {
      fileArray.push(files.item(i));
    }

    const eventName = 'Clicked Upload File';
    logEvent(eventName);
    setUploadProgress(0); // Initialize the upload

    return Promise.resolve(onStart())
      .then(createdUrl => {
        return uploadFileRaw(
          createdUrl || propsUrl || url,
          fileArray,
          params,
          setUploadProgress,
          method,
          additionalFormData,
        );
      })
      .then((res) => {
        logEventSuccess(eventName);
        if (displaySuccessToastMessage && successToastMessage) {
          displaySuccess(successToastMessage, 5000);
        }
        return res;
      })
      .then(onSuccess)
      .catch((err) => {
        logEventFailureWithError(eventName, err);
        const resp = err.response.data;
        const message = resp.errors && resp.errors[0]
          ? resp.errors[0].detail
          : 'Failed to upload file';
        displayErrorMessage(err, message);
        setUploadProgress(null); // <-- Clear the upload bar if upload fails.
        return onCatch();
      })
      .then(() => {
        // Reset the value of the input to prevent it from blocking duplicate files. This
        // is a problem when fileA gets uploaded, deleted, then uploaded again.
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setUploadProgress(null); // Clear the upload bar after the upload is done.
      })
      .then(onFinally, onFinally);
  }

  function handleUploadClick() {
    if (onClick) {
      onClick();
    }
    fileInputRef.current.click();
  }
  const { rootNodeProps } = EventTracking;

  return (
    <React.Fragment key="file-upload-input">
      <input
        style={{
          display: 'none',
        }}
        {...rootNodeProps}
        ref={fileInputRef}
        id="file-input"
        type="file"
        multiple
        accept={accept}
        disabled={!url || disableClick}
        onChange={handleSubmit}
      />
      {React.cloneElement(children, {
        onClick: handleUploadClick,
        disabled: !url || disableClick,
      })}
    </React.Fragment>
  );
};

Uploader2.defaultProps = {
  accept: '',
  onCatch: noop,
  onFinally: noop,
  onStart: async () => { },
  onSuccess: async () => { },
  successToastMessage: 'File(s) uploaded successfully to Secure Files',
  displaySuccessToastMessage: true,
  setUploadProgress: noop,
  method: 'post',
  url: null,
  additionalFormData: {},
  params: {},
  onClick: noop,
  disableClick: false,
};

Uploader2.propTypes = {
  children: PropTypes.node.isRequired,
  setUploadProgress: PropTypes.func,
  url: PropTypes.string,
  onCatch: PropTypes.func,
  onFinally: PropTypes.func,
  onStart: PropTypes.func,
  onSuccess: PropTypes.func,
  successToastMessage: PropTypes.string,
  displaySuccessToastMessage: PropTypes.bool,
  accept: PropTypes.string,
  additionalFormData: PropTypes.object,
  params: PropTypes.shape({
    uploadType: PropTypes.oneOf(Object.values(FILE_UPLOAD_TYPES)),
    folderId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    householdId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }),
  method: PropTypes.string,
  onClick: PropTypes.func,
  disableClick: PropTypes.bool,
  EventTracking: PropTypes.object.isRequired,
};

const eventTrackingProperties = {
  nodeName: 'File Uploader',
};
export default withEventTrackingLogEvent(eventTrackingProperties, Uploader2);
